import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/core/Checkbox.atom';
import { VersionHistoryOperations } from '@/core/hooks/useVersionState.hook';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { InputGroup, Select, TextArea } from '@seeqdev/qomponents';
import type { VersionState } from '@/core/hooks/useVersionState.hook';

interface VersioningSectionProps {
  versionState: VersionState;
  disabled?: boolean;
}

export const VersioningSection = ({ versionState, disabled }: VersioningSectionProps) => {
  const { t } = useTranslation();
  const isTopic = useWorkbookIsTopic();
  const {
    versionHistoryOperation,
    selectedVersionHistory,
    newWorkbookName,
    handleSetNewWorkbookName,
    handleSetNotes,
    handleSetOperation,
    handleSelectVersionHistory,
    writeableVersionHistoryOptions,
  } = versionState;

  return (
    <div {...stylex.props(styles.section)} data-testid="version-history-radio-group">
      <div>
        <Checkbox
          id="radio-version-history-update"
          type="radio"
          disabled={disabled}
          value={VersionHistoryOperations.Update}
          isChecked={versionHistoryOperation === VersionHistoryOperations.Update}
          onChange={() => handleSetOperation(VersionHistoryOperations.Update)}
          label={
            isTopic
              ? t('ACCESS_CONTROL.LOCKING.VERSIONING.UPDATE_EXISTING_TOPIC_VERSION_HISTORY')
              : t('ACCESS_CONTROL.LOCKING.VERSIONING.UPDATE_EXISTING_VERSION_HISTORY')
          }
        />
        <div {...stylex.props(styles.historyField)} data-testid="version-history-select">
          <Select
            options={writeableVersionHistoryOptions}
            value={selectedVersionHistory}
            placeholder={t('ACCESS_CONTROL.LOCKING.VERSIONING.SELECT_VERSION_HISTORY')}
            onChange={(option) => handleSelectVersionHistory(option)}
            isDisabled={versionHistoryOperation !== VersionHistoryOperations.Update || disabled}
            menuPlacement="bottom"
            menuPortalTarget={document.body}
          />
        </div>
      </div>

      <div {...stylex.props(styles.newVersion)}>
        <Checkbox
          id="radio-version-history-new"
          type="radio"
          disabled={disabled}
          value={VersionHistoryOperations.Create}
          isChecked={versionHistoryOperation === VersionHistoryOperations.Create}
          onChange={() => handleSetOperation(VersionHistoryOperations.Create)}
          label={t('ACCESS_CONTROL.LOCKING.VERSIONING.CREATE_NEW_VERSION_HISTORY')}
        />

        <div {...stylex.props(styles.nameField)}>
          <div {...stylex.props(styles.nameRow)}>
            <InputGroup
              type="text"
              value={newWorkbookName}
              disabled={disabled || versionHistoryOperation === VersionHistoryOperations.Update}
              testId="rename-workbook-input"
              onChange={(e) => handleSetNewWorkbookName(e.target.value)}
              placeholder={t('ACCESS_CONTROL.LOCKING.VERSIONING.ENTER_NEW_NAME')}
              append={[]}
              showError={!!versionState.newWorkbookNameError}
            />
            <div {...stylex.props(styles.versionNumber)}>{`${t('ACCESS_CONTROL.LOCKING.VERSIONING.V')}1`}</div>
          </div>
          <div className="error-text">{versionState.newWorkbookNameError}</div>
        </div>

        <div {...stylex.props(styles.notesField)}>
          <div {...stylex.props(styles.notesLabel)}>{t('ACCESS_CONTROL.LOCKING.VERSIONING.VERSION_NOTES_LABEL')}</div>
          <TextArea
            disabled={disabled}
            xstyle={styles.notesInput}
            testId="version-notes-textarea"
            onChange={(e) => handleSetNotes(e.target.value)}
            placeholder={t('ACCESS_CONTROL.LOCKING.VERSIONING.NOTES_PLACEHOLDER')}
          />
        </div>
      </div>
    </div>
  );
};

const styles = stylex.create({
  section: { display: 'flex', flexDirection: 'column' },
  historyField: { marginLeft: '1.25rem', width: 300 },
  newVersion: { marginTop: '1.25rem' },
  nameField: { marginBottom: '1.25rem', marginLeft: '1.25rem', width: 300 },
  nameRow: { display: 'flex', flexDirection: 'row', alignItems: 'flex-end' },
  versionNumber: { marginLeft: '0.3125rem' },
  notesField: { marginTop: '1.25rem', width: 500 },
  notesLabel: { marginBottom: '0.3125rem' },
  notesInput: { width: 500 },
});
